import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardList, Clock, Heart, Activity, ChevronRight, AlertCircle, Calendar } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../store/authStore';
import { getSessions } from '../../api/sessions';
import { SkeletonBlock } from '../../components/SkeletonBlock';
import { Button } from '../../components/Button';
type DateRange = 'today' | '7days' | '30days' | '3months' | 'custom';
export function SessionsList() {
  const navigate = useNavigate();
  const {
    token
  } = useAuth();
  const [sessions, setSessions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activeRange, setActiveRange] = useState<DateRange>('30days');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [showCustomSheet, setShowCustomSheet] = useState(false);
  const fetchSessions = async (range: DateRange, fromDate?: string, toDate?: string) => {
    if (!token) return;
    setIsLoading(true);
    setError(false);
    try {
      const now = new Date();
      let from = new Date();
      let to = new Date();
      if (range === 'today') {
        from.setHours(0, 0, 0, 0);
      } else if (range === '7days') {
        from.setDate(now.getDate() - 7);
        from.setHours(0, 0, 0, 0);
      } else if (range === '30days') {
        from.setDate(now.getDate() - 30);
        from.setHours(0, 0, 0, 0);
      } else if (range === '3months') {
        from.setDate(now.getDate() - 90);
        from.setHours(0, 0, 0, 0);
      } else if (range === 'custom' && fromDate && toDate) {
        from = new Date(fromDate);
        to = new Date(toDate);
        to.setHours(23, 59, 59, 999);
      }
      const res = await getSessions(token, {
        from: from.toISOString(),
        to: to.toISOString()
      });
      setSessions(res || []);
    } catch (err) {
      // console.error('Failed to fetch sessions', err)
      setError(true);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    if (activeRange !== 'custom') {
      fetchSessions(activeRange);
    }
  }, [activeRange, token]);
  const handleCustomApply = () => {
    if (customFrom && customTo) {
      setActiveRange('custom');
      fetchSessions('custom', customFrom, customTo);
      setShowCustomSheet(false);
    }
  };
  // Compute Stats
  const stats = useMemo(() => {
    let totalDuration = 0;
    let bpmSum = 0;
    let bpmCount = 0;
    sessions.forEach(s => {
      if (s.duration) totalDuration += s.duration;
      if (typeof s.avgBpm === 'number' && s.avgBpm > 0) {
        bpmSum += s.avgBpm;
        bpmCount += 1;
      }
    });
    const h = Math.floor(totalDuration / 60);
    const m = totalDuration % 60;
    return {
      total: sessions.length,
      time: `${h}h ${m}m`,
      avgBpm: bpmCount > 0 ? Math.round(bpmSum / bpmCount) : null
    };
  }, [sessions]);
  // Group Sessions by Date
  const groupedSessions = useMemo(() => {
    const groups: Record<string, any[]> = {};
    sessions.forEach(session => {
      const date = new Date(session.createdAt || Date.now());
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      let dateKey = date.toLocaleDateString('en-US', {
        weekday: 'long',
        day: 'numeric',
        month: 'short'
      });
      if (date.toDateString() === today.toDateString()) dateKey = 'Today';else if (date.toDateString() === yesterday.toDateString()) dateKey = 'Yesterday';
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(session);
    });
    return groups;
  }, [sessions]);
  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
  };
  const formatDuration = (minutes: number) => {
    if (!minutes) return '0m';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };
  const getStatusPill = (status: string) => {
    switch (status) {
      case 'active':
        return <span className="bg-danger/15 text-danger px-2 py-0.5 rounded-pill text-[12px] font-medium" data-id="element-1109" data-mp="2od2j72g3y6u5">
            Active
          </span>;
      case 'incomplete':
        return <span className="bg-amber-500/15 text-amber-600 px-2 py-0.5 rounded-pill text-[12px] font-medium" data-id="element-1110" data-mp="3h1zng1nhi33w">
            Incomplete
          </span>;
      default:
        return <span className="bg-accent/20 text-accent px-2 py-0.5 rounded-pill text-[12px] font-medium" data-id="element-1111" data-mp="378957ehyaown">
            Completed
          </span>;
    }
  };
  return <main className="w-full h-full flex flex-col pb-8" data-id="element-1112" data-mp="3m20kplyldac">
      {error && <div className="bg-danger/10 border border-danger/20 rounded-[12px] p-3 mb-6 flex items-center justify-between" data-id="element-1113" data-mp="3uz651swh1vrj">
          <div className="flex items-center text-danger" data-id="element-1114" data-mp="66huwlzj5cx4">
            <AlertCircle size={18} className="mr-2" data-id="element-1115" data-mp="b4jgbdkfhobr" />
            <span className="text-[14px] font-medium" data-id="element-1116" data-mp="b5b2jxs4y9pd">
              Failed to load sessions.
            </span>
          </div>
          <button onClick={() => fetchSessions(activeRange, customFrom, customTo)} className="text-danger text-[14px] font-semibold hover:underline" data-id="element-1117" data-mp="g5p5x2iskvh2">
            Retry
          </button>
        </div>}

      {/* Header */}
      <div className="mb-6" data-id="element-1118" data-mp="u22qikimhczr">
        <h1 className="text-[24px] font-semibold text-text-primary" data-id="element-1119" data-mp="u1asl31jz36b">
          Session History
        </h1>
        <p className="text-[14px] text-text-secondary mt-1" data-id="element-1120" data-mp="ywgei1uyghue">
          {sessions.length} sessions recorded
        </p>
      </div>

      {/* Filter Bar */}
      <div className="flex overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0 hide-scrollbar space-x-2 mb-6" data-id="element-1121" data-mp="18tl308c9wped">
        {[{
        id: 'today',
        label: 'Today'
      }, {
        id: '7days',
        label: 'Last 7 days'
      }, {
        id: '30days',
        label: 'Last 30 days'
      }, {
        id: '3months',
        label: 'Last 3 months'
      }, {
        id: 'custom',
        label: 'Custom range'
      }].map(filter => <button key={filter.id} onClick={() => filter.id === 'custom' ? setShowCustomSheet(true) : setActiveRange(filter.id as DateRange)} className={`flex-shrink-0 h-[36px] px-4 rounded-pill text-[14px] font-medium transition-colors duration-200 ${activeRange === filter.id ? 'bg-primary text-white' : 'bg-border text-text-secondary hover:bg-border/80'}`} data-id="element-1122" data-mp="3skhghjaq6x2v">
            {filter.label}
          </button>)}
      </div>

      {/* Summary Stats */}
      <div className="flex overflow-x-auto pb-4 -mx-4 px-4 md:mx-0 md:px-0 hide-scrollbar space-x-3 mb-6" data-id="element-1123" data-mp="2385mfwbt1rif">
        <div className="bg-white rounded-[16px] shadow-sm p-3 min-w-[140px] flex-shrink-0" data-id="element-1124" data-mp="1t4i5jhr1lvm7">
          <ClipboardList size={20} className="text-primary mb-2" data-id="element-1125" data-mp="1x5u081orx719" />
          {isLoading ? <SkeletonBlock height="24px" width="50%" className="mb-1" data-id="element-1126" data-mp="1ja8d6023oxbh" /> : <div className="text-[22px] font-bold text-text-primary leading-none mb-1" data-id="element-1127" data-mp="1o9kyggdbpwby">
              {stats.total}
            </div>}
          <div className="text-[12px] text-text-secondary" data-id="element-1128" data-mp="1eczorhqczx1s">Total Sessions</div>
        </div>
        <div className="bg-white rounded-[16px] shadow-sm p-3 min-w-[140px] flex-shrink-0" data-id="element-1129" data-mp="10dqbaptjfh1i">
          <Clock size={20} className="text-accent mb-2" data-id="element-1130" data-mp="15cjrwr44fo5w" />
          {isLoading ? <SkeletonBlock height="24px" width="70%" className="mb-1" data-id="element-1131" data-mp="15eedm7n7vkb1" /> : <div className="text-[22px] font-bold text-text-primary leading-none mb-1" data-id="element-1132" data-mp="2upeu532w43r6">
              {stats.time}
            </div>}
          <div className="text-[12px] text-text-secondary" data-id="element-1133" data-mp="c65dwo9jiuzk">Total Time</div>
        </div>
        <div className="bg-white rounded-[16px] shadow-sm p-3 min-w-[140px] flex-shrink-0" data-id="element-1134" data-mp="76svqgtm6c3b">
          <Heart size={20} className="text-primary mb-2" data-id="element-1135" data-mp="2fsux0hdj9vj" />
          {isLoading ? <SkeletonBlock height="24px" width="50%" className="mb-1" data-id="element-1136" data-mp="2dg8aa0o8bc5" /> : <div className="text-[22px] font-bold text-text-primary leading-none mb-1" data-id="element-1137" data-mp="3uhip5m6p8z3s">
              {stats.avgBpm !== null ? stats.avgBpm : '--'}
            </div>}
          <div className="text-[12px] text-text-secondary" data-id="element-1138" data-mp="3kjuji17k276u">Avg BPM</div>
        </div>
      </div>

      {/* Sessions List */}
      <div className="flex-1" data-id="element-1139" data-mp="3fofabmkhszgr">
        {isLoading ? <div className="space-y-4" data-id="element-1140" data-mp="3blsmdwqxzyou">
            {[1, 2, 3, 4, 5].map(i => <SkeletonBlock key={i} height="120px" borderRadius="20px" data-id="element-1141" data-mp="1dv97q7pw3hou" />)}
          </div> : sessions.length === 0 ? <div className="flex flex-col items-center justify-center py-12 text-center" data-id="element-1142" data-mp="19u5rhk3xjjn3">
            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-4" data-id="element-1143" data-mp="2rqhko044pk04">
              <Calendar size={32} className="text-primary" data-id="element-1144" data-mp="2woj08b2cj16u" />
            </div>
            <h2 className="text-[20px] font-semibold text-text-primary mb-2" data-id="element-1145" data-mp="31hvi82cis9qm">
              No sessions found
            </h2>
            <p className="text-[14px] text-text-secondary mb-6 max-w-[250px]" data-id="element-1146" data-mp="35hmaybllwzxs">
              No monitoring sessions recorded for this period.
            </p>
            {activeRange === 'today' && <Button onClick={() => navigate('/app/monitor')} className="max-w-[200px]" data-id="element-1147" data-mp="2drhu2mx22v1b">
                Start Monitoring
              </Button>}
          </div> : <div className="space-y-8" data-id="element-1148" data-mp="3t88js53fqo8u">
            {Object.entries(groupedSessions).map(([dateLabel, groupSessions]) => <div key={dateLabel} data-id="element-1149" data-mp="20dvtzi9ke6r">
                  <div className="flex items-center mb-4" data-id="element-1150" data-mp="23iaxrx9ouc7">
                    <span className="text-[13px] font-semibold text-text-secondary uppercase tracking-[1px] mr-4" data-id="element-1151" data-mp="34rc0i1e1nn4p">
                      {dateLabel}
                    </span>
                    <div className="flex-1 h-[1px] bg-border" data-id="element-1152" data-mp="3ehomly1ibocy" />
                  </div>
                  <div className="space-y-3" data-id="element-1153" data-mp="3jhjcpzdgk9wy">
                    {groupSessions.map((session, i) => <div key={session.id || i} onClick={() => navigate(`/app/history/${session.id}`)} className="bg-white rounded-[20px] shadow-sm p-4 cursor-pointer hover:-translate-y-[2px] hover:shadow-card transition-all" data-id="element-1154" data-mp="2lu2j0psa4jq3" data-mpi={i}>
                        <div className="flex items-center justify-between mb-4" data-id="element-1155" data-mp="2zttqs8s7d3vn">
                          <div className="flex items-center" data-id="element-1156" data-mp="b1zq3j7alpqv">
                            <div className="w-[44px] h-[44px] bg-primary rounded-full flex items-center justify-center mr-4 flex-shrink-0" data-id="element-1157" data-mp="f2tbo5x9isgl">
                              <Activity size={20} className="text-white" data-id="element-1158" data-mp="1zjsrfzt85crp" />
                            </div>
                            <div data-id="element-1159" data-mp="1vkjxatu9vcvm">
                              <h3 className="text-[15px] font-semibold text-text-primary" data-id="element-1160" data-mp="1vld4tx1226et">
                                {new Date(session.createdAt || Date.now()).toLocaleDateString('en-US', {
                        weekday: 'short',
                        day: 'numeric',
                        month: 'short'
                      })}{' '}
                                · {formatTime(session.createdAt || Date.now())}
                              </h3>
                              <p className="text-[13px] text-text-secondary mt-0.5" data-id="element-1161" data-mp="12w5xc8jmi6hf">
                                {formatDuration(session.duration)}
                              </p>
                            </div>
                          </div>
                          <ChevronRight size={20} className="text-text-secondary" data-id="element-1162" data-mp="3gdhdbqgxhp57" />
                        </div>

                        <div className="h-[1px] w-full bg-border mb-3" data-id="element-1163" data-mp="37cszowvg5qsu" />

                        <div className="flex items-center justify-between px-2" data-id="element-1164" data-mp="37agapzr9ug6a">
                          <div className="flex flex-col items-center" data-id="element-1165" data-mp="3cc5ohn5vp7y2">
                            <span className="text-[11px] text-text-secondary uppercase tracking-wider mb-1" data-id="element-1166" data-mp="3wvn6h9tc6rc">
                              Avg BPM
                            </span>
                            <span className="text-[13px] font-semibold text-text-primary" data-id="element-1167" data-mp="3q3k73svvj74l">
                              {typeof session.avgBpm === 'number' && session.avgBpm > 0 ? Math.round(session.avgBpm) : '--'}
                            </span>
                          </div>
                          <div className="w-[1px] h-[24px] bg-border" data-id="element-1168" data-mp="2c3fnuzlhnyed" />
                          <div className="flex flex-col items-center" data-id="element-1169" data-mp="2c1mgfi31xa5b">
                            <span className="text-[11px] text-text-secondary uppercase tracking-wider mb-1" data-id="element-1170" data-mp="273b7raoht5oo">
                              Duration
                            </span>
                            <span className="text-[13px] font-semibold text-text-primary" data-id="element-1171" data-mp="2g4stb8ko5zkr">
                              {formatDuration(session.duration)}
                            </span>
                          </div>
                          <div className="w-[1px] h-[24px] bg-border" data-id="element-1172" data-mp="1of64e4bqzmqe" />
                          <div className="flex flex-col items-center" data-id="element-1173" data-mp="225t2bu9op8lf">
                            <span className="text-[11px] text-text-secondary uppercase tracking-wider mb-1" data-id="element-1174" data-mp="1xeji596wmxgx">
                              Status
                            </span>
                            {getStatusPill(session.sessionStatus || 'completed')}
                          </div>
                        </div>
                      </div>)}
                  </div>
                </div>)}
          </div>}
      </div>

      {/* Custom Date Range Bottom Sheet */}
      <AnimatePresence data-id="element-1175" data-mp="1s8pinrpm0y6m">
        {showCustomSheet && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowCustomSheet(false)} className="fixed inset-0 bg-black/40 z-[110]" data-id="element-1176" data-mp="1x320xyfco3gs" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed bottom-0 left-0 right-0 bg-white rounded-t-[24px] p-6 pb-[calc(24px+env(safe-area-inset-bottom))] z-[120] md:max-w-[400px] md:left-1/2 md:-translate-x-1/2 md:bottom-6 md:rounded-[24px]" data-id="element-1177" data-mp="2g0wjb32dbrbu">
              <div className="w-[40px] h-[4px] bg-border rounded-pill mx-auto mb-6" data-id="element-1178" data-mp="2l5gt7rnlb6xg" />
              <h2 className="text-[20px] font-semibold text-text-primary mb-6" data-id="element-1179" data-mp="3hvhcjb56wv7d">
                Custom Date Range
              </h2>

              <div className="flex space-x-4 mb-8" data-id="element-1180" data-mp="3dtmau5jckft2">
                <div className="flex-1" data-id="element-1181" data-mp="3ds1jspw8rezy">
                  <label htmlFor="customFrom" className="block text-[13px] text-text-secondary mb-2" data-id="element-1182" data-mp="38u00c3uwldny">
                    From
                  </label>
                  <input id="customFrom" type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} className="w-full h-[52px] rounded-input border border-border px-4 text-text-primary focus:border-primary focus:shadow-focus outline-none transition-all" data-id="element-1183" data-mp="1xgnvpqrbkr0q" />
                </div>
                <div className="flex-1" data-id="element-1184" data-mp="1kwifbb360q21">
                  <label htmlFor="customTo" className="block text-[13px] text-text-secondary mb-2" data-id="element-1185" data-mp="1pw3ixyta5ml3">
                    To
                  </label>
                  <input id="customTo" type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} className="w-full h-[52px] rounded-input border border-border px-4 text-text-primary focus:border-primary focus:shadow-focus outline-none transition-all" data-id="element-1186" data-mp="1fxnq9relu59h" />
                </div>
              </div>

              <div className="flex flex-col space-y-3" data-id="element-1187" data-mp="x6edn99jf0do">
                <Button onClick={handleCustomApply} disabled={!customFrom || !customTo} data-id="element-1188" data-mp="dlnykwh2j60k">
                  Apply
                </Button>
                <button onClick={() => setShowCustomSheet(false)} className="w-full h-[52px] text-text-secondary font-medium rounded-btn hover:bg-border/50 transition-colors" data-id="element-1189" data-mp="8oxiwa96ecfo">
                  Cancel
                </button>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>

      <style data-id="element-1190" data-mp="rkvyjrl22i5t">{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </main>;
}